import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
    Sparkles, Map as MapIcon, RefreshCcw, Wallet, Globe, BarChart3,
    ArrowRight, Check, Cpu, Plug, Layers, Zap, ShieldCheck, Phone, FileStack, Play,
    FileBarChart, HardHat, MapPinned, MessageSquareText, FileDown, Languages,
} from "lucide-react";
import { useLanguage, LangSwitcher } from "@/context/LanguageContext";
import { tr } from "@/lib/i18n";
import { APP_VERSION } from "@/lib/version";

const LOGO = "/assets/logo.png";
const BG = "/assets/aerial-bg.jpg";

const FEATURES = [
    { icon: Layers, title: "landing.feat1_t", desc: "landing.feat1_d" },
    { icon: RefreshCcw, title: "landing.feat2_t", desc: "landing.feat2_d" },
    { icon: MapIcon, title: "landing.feat3_t", desc: "landing.feat3_d" },
    { icon: Wallet, title: "landing.feat4_t", desc: "landing.feat4_d" },
    { icon: Globe, title: "landing.feat6_t", desc: "landing.feat6_d" },
    { icon: BarChart3, title: "landing.feat5_t", desc: "landing.feat5_d" },
    { icon: FileBarChart, title: "landing.new1_t", desc: "landing.new1_d", isNew: true },
    { icon: HardHat, title: "landing.new2_t", desc: "landing.new2_d", isNew: true },
    { icon: MapPinned, title: "landing.new3_t", desc: "landing.new3_d", isNew: true },
    { icon: MessageSquareText, title: "landing.new4_t", desc: "landing.new4_d", isNew: true },
    { icon: FileDown, title: "landing.new5_t", desc: "landing.new5_d", isNew: true },
    { icon: Languages, title: "landing.new6_t", desc: "landing.new6_d", isNew: true },
];

const AI_FEATURES = ["landing.ai1", "landing.ai2", "landing.ai3", "landing.ai4"];

const HERO_STATS = [
    { target: 12, format: (n) => `${Math.round(n)}`, label: "landing.stat_files", icon: FileStack },
    { target: 2353, format: (n) => Math.round(n).toLocaleString("en-CA"), label: "landing.stat_units", icon: BarChart3 },
    { target: 3.1, format: (n) => `$${n.toFixed(1)}M`, label: "landing.stat_fees", icon: Wallet },
    { target: 14, format: (n) => `${Math.round(n)}`, label: "landing.stat_agencies", icon: RefreshCcw },
];

const TICKER = [
    { color: "#06D6A0", key: "landing.tick1" },
    { color: "#0A2540", key: "landing.tick2" },
    { color: "#06D6A0", key: "landing.tick3" },
    { color: "#00B4D8", key: "landing.tick4" },
    { color: "#7B61FF", key: "landing.tick5" },
    { color: "#06D6A0", key: "landing.tick6" },
    { color: "#FFB703", key: "landing.tick7" },
    { color: "#00B4D8", key: "landing.tick8" },
];

function CountUp({ target, format }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-40px" });
    const [val, setVal] = useState(0);
    useEffect(() => {
        if (!inView) return;
        const start = performance.now();
        const dur = 1400;
        let raf;
        const tick = (now) => {
            const p = Math.min(1, (now - start) / dur);
            setVal(target * (1 - Math.pow(1 - p, 3)));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [inView, target]);
    return <span ref={ref}>{format(val)}</span>;
}

function Typewriter({ text }) {
    const ref = useRef(null);
    const inView = useInView(ref, { margin: "-60px" });
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!inView) return;
        setN(0);
        let i = 0;
        const iv = setInterval(() => {
            i += 1;
            setN(i);
            if (i >= text.length) clearInterval(iv);
        }, 18);
        return () => clearInterval(iv);
    }, [inView, text]);
    return (
        <span ref={ref}>
            {text.slice(0, n)}
            {n < text.length && <span className="inline-block w-[2px] h-[1em] align-[-0.15em] bg-[#2DD4BF] animate-pulse ml-0.5" />}
        </span>
    );
}

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" } };

export default function Landing() {
    const { t } = useLanguage();
    return (
        <div className="min-h-screen relative text-[#0A2540]" data-testid="landing-page">
            {/* Full-bleed aerial backdrop */}
            <div className="fixed inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }} />
            <div className="fixed inset-0 bg-[#F4F8FA]/70" />
            <div className="fixed inset-0" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(240,247,250,0.5) 100%)" }} />

            <div className="relative">
                {/* ── Hero: Istanbul-Lines-style glass shell with navy rail ── */}
                <div className="max-w-[1280px] mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-10">
                    <motion.div
                        initial={{ opacity: 0, y: 32, scale: 0.99 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        className="rounded-[28px] overflow-hidden shadow-[0_40px_90px_-30px_rgba(10,37,64,0.45)] border border-white/60 flex flex-col lg:flex-row"
                        data-testid="hero-shell"
                    >
                        {/* Left navy rail */}
                        <aside className="lg:w-[300px] shrink-0 bg-[#0A2540]/95 backdrop-blur-xl text-white p-7 sm:p-8 flex flex-col relative overflow-hidden">
                            <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#00B4D8]/20 blur-3xl pointer-events-none" />
                            <Link to="/" className="block relative" data-testid="landing-logo">
                                <img src="/assets/logo-light.png" alt="LandLogic NexusOne" className="h-16 w-auto drop-shadow-lg" />
                            </Link>

                            <div className="mt-10 relative">
                                <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#7CD4EA]">{t("landing.rail_where")}</div>
                                <nav className="mt-4 space-y-1">
                                    {[
                                        ["#features", t("landing.rail_platform"), Layers],
                                        ["#ai", t("landing.rail_ai"), Sparkles],
                                        ["#integrations", t("landing.rail_integrations"), Plug],
                                        ["/portal", t("landing.rail_register"), FileStack],
                                        ["/developer", t("landing.rail_dev"), Globe],
                                    ].map(([href, label, Icon]) => (
                                        href.startsWith("/") ? (
                                            <Link key={label} to={href} data-testid={`rail-nav-${href.replace(/[/]/g, "").toLowerCase()}`}
                                                className="flex items-center gap-3 px-3.5 py-3 rounded-[10px] text-[13px] font-medium text-white/65 hover:text-white hover:bg-white/10 transition-all group">
                                                <Icon className="w-4 h-4 text-[#7CD4EA] group-hover:scale-110 transition-transform" /> {label}
                                                <ArrowRight className="w-3.5 h-3.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </Link>
                                        ) : (
                                            <a key={label} href={href} data-testid={`rail-nav-${href.replace(/[#]/g, "").toLowerCase()}`}
                                                className="flex items-center gap-3 px-3.5 py-3 rounded-[10px] text-[13px] font-medium text-white/65 hover:text-white hover:bg-white/10 transition-all group">
                                                <Icon className="w-4 h-4 text-[#7CD4EA] group-hover:scale-110 transition-transform" /> {label}
                                                <ArrowRight className="w-3.5 h-3.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </a>
                                        )
                                    ))}
                                </nav>
                            </div>

                            <div className="mt-auto pt-10 relative">
                                <div className="rounded-[14px] bg-white/[0.06] border border-white/10 p-4">
                                    <div className="flex items-center gap-2 text-[11px] text-white/50">
                                        <Phone className="w-3.5 h-3.5 text-[#2DD4BF]" /> {t("landing.city_tag")}
                                    </div>
                                    <div className="mt-2 text-[13px] text-white/80 leading-relaxed">{t("landing.city_desc")}</div>
                                </div>
                                <div className="mt-4 text-[10px] text-white/30 font-data">{t("landing.footer_tag")}</div>
                            </div>
                        </aside>

                        {/* Right frosted content */}
                        <div className="flex-1 bg-white/70 backdrop-blur-2xl relative">
                            {/* top bar */}
                            <div className="flex items-center justify-between gap-4 px-7 sm:px-10 pt-7">
                                <div className="hidden md:flex items-center gap-7 text-[13px] font-medium text-slate-500">
                                    <a href="#features" className="hover:text-[#0092B0] transition-colors">{t("landing.features")}</a>
                                    <a href="#ai" className="hover:text-[#0092B0] transition-colors">AI</a>
                                    <a href="#integrations" className="hover:text-[#0092B0] transition-colors">{t("landing.integrations")}</a>
                                    <Link to="/portal" data-testid="landing-public-register" className="hover:text-[#0092B0] transition-colors">{t("landing.public_register")}</Link>
                                </div>
                                <div className="flex items-center gap-2.5 ml-auto">
                                    <LangSwitcher testid="landing-lang-switcher" />
                                    <Link to="/login" data-testid="landing-signin-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[8px] text-[13px] font-semibold text-white bg-[#0A2540] hover:bg-[#123a5f] transition-all hover:shadow-lg active:scale-[0.98]">
                                        <ShieldCheck className="w-4 h-4 text-[#2DD4BF]" /> {t("common.signin")}
                                    </Link>
                                </div>
                            </div>

                            <div className="px-7 sm:px-10 pt-10 sm:pt-14 pb-10">
                                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.6 }}>
                                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#00B4D8]/10 border border-[#00B4D8]/25 text-[11px] font-semibold text-[#0092B0]">
                                        <span className="relative flex w-2 h-2">
                                            <span className="absolute inline-flex w-full h-full rounded-full bg-[#2DD4BF] opacity-75 animate-ping" />
                                            <span className="relative inline-flex w-2 h-2 rounded-full bg-[#2DD4BF]" />
                                        </span>
                                        <Sparkles className="w-3.5 h-3.5" /> {t("landing.hero_tag")}
                                    </div>
                                    <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[54px] font-extrabold tracking-tight font-heading leading-[1.04] text-[#0A2540]">
                                        {t("landing.hero_1")}<br />
                                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00B4D8] to-[#2DD4BF]">{t("landing.hero_2")}</span>
                                    </h1>
                                    <p className="mt-5 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
                                        {t("landing.hero_desc")}
                                    </p>
                                    <div className="mt-8 flex flex-wrap gap-3">
                                        <Link to="/login" data-testid="hero-cta-demo" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-[10px] font-semibold text-sm text-white bg-[#00B4D8] hover:bg-[#00c3eb] transition-all hover:shadow-[0_12px_30px_-8px_rgba(0,180,216,0.6)] hover:-translate-y-0.5 active:scale-[0.98]">
                                            {t("landing.cta_demo")} <ArrowRight className="w-4 h-4" />
                                        </Link>
                                        <Link to="/developer" data-testid="hero-cta-portal" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-[10px] font-semibold text-sm bg-white/80 border border-slate-200 hover:border-[#00B4D8] hover:text-[#0092B0] transition-all hover:-translate-y-0.5 active:scale-[0.98]">
                                            <Globe className="w-4 h-4" /> {t("landing.cta_portal")}
                                        </Link>
                                        <Link to="/tour" data-testid="hero-cta-tour" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-[10px] font-semibold text-sm bg-[#0A2540] text-white hover:bg-[#123a5f] transition-all hover:-translate-y-0.5 active:scale-[0.98]">
                                            <Play className="w-4 h-4" /> {t("landing.cta_tour")}
                                        </Link>
                                    </div>
                                </motion.div>

                                {/* stat tiles */}
                                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }}
                                    className="mt-10 grid grid-cols-2 xl:grid-cols-4 gap-3" data-testid="hero-stats">
                                    {HERO_STATS.map(({ target, format, label, icon: Icon }) => (
                                        <div key={label} className="bg-white/80 backdrop-blur rounded-[14px] border border-white p-4 shadow-[0_8px_24px_-10px_rgba(10,37,64,0.15)] hover:shadow-[0_16px_36px_-12px_rgba(10,37,64,0.25)] hover:-translate-y-0.5 transition-all">
                                            <Icon className="w-4 h-4 text-[#00B4D8]" />
                                            <div className="mt-2.5 font-data font-bold text-2xl text-[#0A2540]"><CountUp target={target} format={format} /></div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">{t(label)}</div>
                                        </div>
                                    ))}
                                </motion.div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* ── Live activity ticker ── */}
                <div className="relative overflow-hidden py-3 border-y border-white/60 bg-white/60 backdrop-blur-md" data-testid="activity-ticker">
                    <div className="flex w-max animate-ticker gap-10 px-5">
                        {[...TICKER, ...TICKER].map((item, i) => (
                            <span key={i} className="flex items-center gap-2 text-xs font-medium text-slate-600 whitespace-nowrap">
                                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                                {t(item.key)}
                            </span>
                        ))}
                    </div>
                </div>

                {/* ── Features ── */}
                <section id="features" className="max-w-[1280px] mx-auto px-4 sm:px-6 py-20">
                    <motion.div {...fadeUp} className="max-w-2xl">
                        <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#0092B0]">{t("landing.platform_tag")}</div>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight font-heading">{t("landing.platform_h2")}</h2>
                        <p className="mt-4 text-slate-600">{t("landing.platform_p")}</p>
                    </motion.div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {FEATURES.map((f, i) => (
                            <motion.div key={f.title} {...fadeUp} transition={{ delay: i * 0.06 }}
                                className="relative bg-white/85 backdrop-blur rounded-[16px] border border-white/70 p-6 shadow-[0_8px_28px_-12px_rgba(10,37,64,0.18)] hover:shadow-[0_20px_44px_-14px_rgba(10,37,64,0.3)] hover:-translate-y-1.5 transition-all duration-300 overflow-hidden"
                                data-testid={`feature-card-${f.title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`}>
                                {f.isNew && (
                                    <span className="absolute top-4 right-4 text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-[#2DD4BF]/15 text-[#0E9F85]">{t("landing.new_badge")}</span>
                                )}
                                <div className="w-11 h-11 rounded-[12px] bg-[#0A2540] flex items-center justify-center">
                                    <f.icon className="w-5 h-5 text-[#2DD4BF]" />
                                </div>
                                <h3 className="mt-4 font-heading font-bold text-lg">{t(f.title)}</h3>
                                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t(f.desc)}</p>
                            </motion.div>
                        ))}
                    </div>
                    <motion.div {...fadeUp} className="mt-8 flex flex-wrap items-center gap-2.5">
                        <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400 mr-1">{t("landing.new_more")}</span>
                        {[1, 2, 3, 4, 5, 6].map((n) => (
                            <span key={n} className="text-xs font-medium px-3 py-1.5 rounded-full bg-white/80 border border-white/70 text-slate-600 shadow-sm" data-testid={`new-chip-${n}`}>{t(`landing.chip${n}`)}</span>
                        ))}
                    </motion.div>
                </section>

                {/* ── AI section ── */}
                <section id="ai" className="max-w-[1280px] mx-auto px-4 sm:px-6 pb-20">
                    <motion.div {...fadeUp} className="rounded-[28px] bg-[#0A2540]/95 backdrop-blur-xl text-white overflow-hidden relative border border-white/10 shadow-[0_40px_90px_-30px_rgba(10,37,64,0.5)]">
                        <div className="absolute inset-0 opacity-25 pointer-events-none" style={{ backgroundImage: "radial-gradient(ellipse 50% 60% at 85% 20%, #00B4D8 0%, transparent 60%), radial-gradient(ellipse 35% 45% at 5% 95%, #2DD4BF 0%, transparent 55%)" }} />
                        <div className="relative grid lg:grid-cols-2 gap-12 items-center p-8 sm:p-12 lg:p-14">
                            <div>
                                <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#7CD4EA]">{t("landing.ai_tag")}</div>
                                <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight font-heading">{t("landing.ai_h2")}</h2>
                                <p className="mt-4 text-slate-300 leading-relaxed">{t("landing.ai_p")}</p>
                                <ul className="mt-7 space-y-3.5">
                                    {AI_FEATURES.map((a) => (
                                        <li key={a} className="flex items-start gap-3 text-sm text-slate-200">
                                            <span className="mt-0.5 w-5 h-5 rounded-full bg-[#2DD4BF]/15 flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-[#2DD4BF]" /></span>
                                            {t(a)}
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-8 flex items-center gap-3 text-xs text-slate-400">
                                    <Cpu className="w-4 h-4 text-[#FFB703]" />
                                    {t("landing.ai_models")}
                                </div>
                            </div>
                            <motion.div {...fadeUp} transition={{ delay: 0.15 }} className="rounded-[18px] bg-white/[0.07] backdrop-blur-xl p-6 border border-white/12 shadow-2xl" data-testid="ai-demo-card">
                                <div className="flex items-center gap-2.5 pb-4 border-b border-white/10">
                                    <div className="w-8 h-8 rounded-[8px] bg-[#00B4D8] flex items-center justify-center pulse-ring"><Sparkles className="w-4 h-4 text-white" /></div>
                                    <div>
                                        <div className="font-heading font-semibold text-sm">NexusOne AI</div>
                                        <div className="text-[11px] text-slate-400">context: 27T-26-001 · Maple Grove Estates</div>
                                    </div>
                                </div>
                                <div className="py-4 space-y-3 text-sm">
                                    <div className="flex justify-end"><div className="bg-[#00B4D8] rounded-[12px] rounded-br-[4px] px-4 py-2.5 max-w-[85%]">{t("landing.ai_demo_user")}</div></div>
                                    <div className="bg-white/10 rounded-[12px] rounded-bl-[4px] px-4 py-3 max-w-[92%] text-slate-200 leading-relaxed">
                                        <div className="font-semibold text-white mb-1.5">{t("landing.ai_demo_overall")}</div>
                                        <Typewriter text={tr("4 of 5 agencies responded. No objections from Fire or Transportation…")} />
                                        <div className="font-semibold text-white mt-3 mb-1.5">{t("landing.ai_demo_key")}</div>
                                        <span className="text-[#FFB703]">CVC</span> {tr("requests a 10 m hazard-land setback and a revised grading plan before the public meeting…")}
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </section>

                {/* ── Integrations ── */}
                <section id="integrations" className="max-w-[1280px] mx-auto px-4 sm:px-6 pb-20">
                    <motion.div {...fadeUp} className="max-w-2xl">
                        <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#0092B0]">{t("landing.int_tag")}</div>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight font-heading">{t("landing.int_h2")}</h2>
                    </motion.div>
                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[
                            { icon: Zap, name: "landing.int1_t", desc: "landing.int1_d" },
                            { icon: Cpu, name: "landing.int2_t", desc: "landing.int2_d" },
                            { icon: MapIcon, name: "landing.int3_t", desc: "landing.int3_d" },
                            { icon: Plug, name: "landing.int4_t", desc: "landing.int4_d" },
                            { icon: ShieldCheck, name: "landing.int5_t", desc: "landing.int5_d" },
                            { icon: Layers, name: "landing.int6_t", desc: "landing.int6_d" },
                        ].map((i, n) => (
                            <motion.div key={i.name} {...fadeUp} transition={{ delay: n * 0.05 }}
                                className="flex gap-4 p-5 rounded-[16px] border border-white/70 bg-white/85 backdrop-blur shadow-[0_8px_28px_-12px_rgba(10,37,64,0.18)] hover:-translate-y-1 transition-all duration-300">
                                <div className="w-10 h-10 rounded-[10px] bg-[#0A2540] flex items-center justify-center shrink-0"><i.icon className="w-5 h-5 text-[#2DD4BF]" /></div>
                                <div>
                                    <div className="font-heading font-bold">{t(i.name)}</div>
                                    <div className="text-sm text-slate-600 mt-1 leading-relaxed">{t(i.desc)}</div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>

                {/* ── Portal CTA ── */}
                <section id="portal" className="max-w-[1280px] mx-auto px-4 sm:px-6 pb-24">
                    <motion.div {...fadeUp} className="rounded-[28px] bg-white/85 backdrop-blur-xl border border-white/70 shadow-[0_30px_70px_-25px_rgba(10,37,64,0.35)] p-10 sm:p-14 text-center">
                        <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#0092B0]">{t("landing.cta2_tag")}</div>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight font-heading">{t("landing.cta2_h2")}</h2>
                        <p className="mt-4 text-slate-600 max-w-2xl mx-auto">{t("landing.cta2_p")}</p>
                        <div className="mt-9 flex justify-center gap-3 flex-wrap">
                            <Link to="/developer" data-testid="portal-cta-btn" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-[10px] font-semibold text-sm text-white bg-[#00B4D8] hover:bg-[#00c3eb] transition-all hover:shadow-[0_12px_30px_-8px_rgba(0,180,216,0.6)] hover:-translate-y-0.5 active:scale-[0.98]">
                                {t("landing.cta2_open")} <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link to="/login" data-testid="footer-signin-btn" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-[10px] font-semibold text-sm border border-slate-300 hover:border-[#00B4D8] hover:text-[#0092B0] transition-all hover:-translate-y-0.5">
                                {t("landing.cta_staff")}
                            </Link>
                        </div>
                    </motion.div>
                </section>

                <footer className="border-t border-white/60 bg-[#0A2540]/90 backdrop-blur-xl text-slate-400 py-8">
                    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
                        <div className="flex items-center gap-2.5">
                            <img src={LOGO} alt="LandLogic NexusOne" className="h-8 w-auto" />
                            <span>LandLogic NexusOne {APP_VERSION} · {t("landing.footer_version")}</span>
                        </div>
                        <span className="font-data">{t("landing.footer_tag")}</span>
                    </div>
                </footer>
            </div>
        </div>
    );
}
